/**
 * What: Owns the real Settings connection actions and cleanup recovery, including while disconnected.
 * Transport / data-flow: Shared authenticated connection query/mutations and existing store-data queries feed SettingsPage.
 * Invariants: One page lifecycle owns action feedback; confirmation captures the displayed activation.
 * A failed/unknown unlink always refreshes authority before another action; refresh never replays a write.
 */
import type { useAppBridge } from '@shopify/app-bridge-react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { useShopifyPlanSelectionUrl } from '../../hooks/useShopifyPlanSelectionUrl'
import { useStoreIntelligence } from '../../hooks/useStoreIntelligence'
import { useShopifyConnection } from '../../hooks/useShopifyConnection'
import { useShopifyConnectionMutations } from '../../hooks/useShopifyConnectionMutations'
import { useShopifyScopeLifecycle } from '../../hooks/useShopifyScopeLifecycle'
import { usePageLifecycle } from '../../hooks/usePageLifecycle'
import { page } from '../../pages/settings.page'
import { SettingsPage, type SettingsData, type SettingsLoadState } from './SettingsPage'
import { trackShopifyRefetch } from '../../shopifyUiTracking'

type SettingsRouteProps = {
  shopify: ReturnType<typeof useAppBridge>
  expectedShop: string | null
}

export function SettingsRoute({ shopify, expectedShop }: SettingsRouteProps) {
  const connection = useShopifyConnection(shopify, expectedShop)
  const readiness = connection.readiness
  const visit = JSON.stringify([
    expectedShop,
    readiness?.orgId,
    readiness?.connectionId,
    readiness?.connectionActivatedAt,
  ])
  const scope = useShopifyScopeLifecycle(visit)
  const lifecycle = usePageLifecycle(page, visit, shopify, { connection })
  const mutations = useShopifyConnectionMutations({
    shopify,
    verifiedShop: connection.verifiedShop,
    connectionScope: connection.scope,
    activeShopScopeKey: scope.token,
    isActiveShopScope: scope.isActive,
  })
  const disconnecting = lifecycle.button('disconnect').loading || lifecycle.button('retryCleanup').loading
  const isTestFeedConnected = Boolean(connection.scope) && !connection.isError && !disconnecting
  const storeIntelligence = useStoreIntelligence(shopify, {
    isTestFeedConnected,
    scope: connection.scope ?? undefined,
    includeShopSettings: true,
  })
  const { data: planSelectionUrl } = useShopifyPlanSelectionUrl(shopify, Boolean(connection.verifiedShop))
  const copy = shopifyComponentCopy.settingsIndex
  const actions = {
    button: (action: 'connect' | 'disconnect' | 'retryCleanup') => {
      const state = lifecycle.button(action)
      return { ...state, disabled: state.disabled || connection.isFetching || connection.isError }
    },
  }
  const connect = () => {
    if (!readiness || connection.isFetching || connection.isError) return
    void lifecycle.run('connect', (signal) => mutations.connectTestFeed(signal), readiness)
  }
  const disconnect = (action: 'disconnect' | 'retryCleanup') => {
    if (!readiness?.connectionId || !readiness.connectionActivatedAt || connection.isFetching || connection.isError)
      return
    const target = { connectionId: readiness.connectionId, expectedActivatedAt: readiness.connectionActivatedAt }
    void lifecycle.run(action, (signal) => mutations.disconnectTestFeed(target, signal), readiness)
  }
  const refresh = () => {
    lifecycle.retryFailedLoad()
    void trackShopifyRefetch(shopify, () => connection.refetch())
  }
  let connectionError =
    lifecycle.feedback
      .map((failure) => {
        if (failure.message) return failure.message
        if (failure.operation === 'connect') return copy.connectFailed
        if (failure.operation === 'retryCleanup') return copy.cleanupRetryFailed
        return copy.disconnectFailed
      })
      .join(' ') || null
  if (connection.isError) connectionError = copy.staleConnection
  else if (connection.isDisconnectUnconfirmed) connectionError = copy.disconnectUnconfirmed
  else if (connection.isFetching && readiness) connectionError = copy.refreshingConnection

  let state: SettingsLoadState =
    lifecycle.state.status === 'loading'
      ? { kind: 'loading' }
      : {
          kind: 'ready',
          data: {
            readiness,
            isLoadingReadiness: connection.isFetching || connection.isError,
            isConnecting: lifecycle.button('connect').loading,
            isDisconnecting: disconnecting,
            isDisconnectUnconfirmed: connection.isDisconnectUnconfirmed,
            connectionError,
            planSelectionUrl,
            shopSettings: storeIntelligence.shopSettings,
            isSyncing: storeIntelligence.isSyncing,
            buildStages: storeIntelligence.syncStatusResponse?.buildProgress?.stages ?? null,
            syncStatus: storeIntelligence.syncStatusResponse,
          } satisfies SettingsData,
        }
  if (!expectedShop || lifecycle.state.status === 'error' || (!readiness && connection.isError))
    state = { kind: 'error', message: copy.pageErrorBody }

  return (
    <SettingsPage
      state={state}
      onConnect={connect}
      onDisconnect={() => disconnect('disconnect')}
      onRetryCleanup={() => disconnect('retryCleanup')}
      lifecycle={actions}
      onRetryLoad={refresh}
      isRefreshing={connection.isFetching}
      shopify={shopify}
      isTestFeedConnected={isTestFeedConnected}
    />
  )
}
